import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Mail, Loader2, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { signInWithEmail, signInWithDiscord } from "../lib/api";
import { toast } from "sonner";

interface SignInProps {
  onNavigate?: (page: string) => void;
}

export function SignIn({ onNavigate }: SignInProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);

    try {
      await signInWithEmail(email);
      setEmailSent(true);
      toast.success("Check your email for the magic link!");
    } catch (error: any) {
      console.error("Sign in error:", error);
      toast.error(error.message || "Failed to send magic link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscordSignIn = async () => {
    setOauthLoading(true);
    try {
      await signInWithDiscord();
      // Supabase will handle the redirect; no further action needed here.
    } catch (error: any) {
      console.error("Discord sign in error:", error);
      toast.error(error.message || "Failed to start Discord sign in. Please try again.");
    } finally {
      setOauthLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="p-10 glass-card text-center">
              <div className="w-16 h-16 rounded-full gradient-green flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Mail className="w-8 h-8 text-white" />
              </div>

              <h2 className="text-3xl mb-3">Check your email</h2>

              <p className="text-muted-foreground mb-6">
                We've sent a magic link to <strong>{email}</strong>
              </p>

              <div className="p-4 rounded-xl bg-accent/30 border border-primary/20 mb-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Click the link in the email to sign in. The link will expire in 1 hour.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setEmailSent(false)}
                >
                  Resend email
                </Button>
                <button
                  onClick={() => onNavigate && onNavigate("signup")}
                  className="text-sm text-primary hover:underline w-full"
                >
                  Don't have an account? Sign up
                </button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center shadow-lg">
                <Leaf className="w-7 h-7 text-white" />
              </div>
            </div>
            <h1 className="text-3xl mb-2">Welcome back</h1>
            <p className="text-muted-foreground">
              Sign in to CannaBias
            </p>
          </div>

          {/* Auth options */}
          <Card className="p-8 glass-card">
            {/* Social login */}
            <div className="space-y-4">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 flex items-center justify-center gap-2"
                onClick={handleDiscordSignIn}
                disabled={loading || oauthLoading}
              >
                {oauthLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connecting to Discord...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4" />
                    Continue with Discord
                  </>
                )}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase text-muted-foreground">
                  <span className="px-2 bg-card">Or continue with email</span>
                </div>
              </div>
            </div>

            {/* Email magic link form */}
            <form onSubmit={handleSubmit} className="mt-6 space-y-6">
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2"
                  required
                  disabled={loading || oauthLoading}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-2">
                  We'll send you a magic link to sign in
                </p>
              </div>

              <Button
                type="submit"
                className="w-full gradient-green hover:opacity-90 transition-opacity h-12"
                disabled={loading || oauthLoading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending magic link...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send magic link
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{" "}
                <button
                  onClick={() => onNavigate && onNavigate("signup")}
                  className="text-primary hover:underline"
                  disabled={loading || oauthLoading}
                >
                  Sign up
                </button>
              </p>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-accent/30 border border-primary/20">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Secure passwordless authentication. Your email is the only credential you need.
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
